import React from 'react';

import { useCompanyHubPremiumGate } from '@confluence/company-hub-utils/entry-points/useCompanyHubPremiumGate.ts';
import { MainLayout } from '@confluence/main-layout';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { Screen } from '@confluence/screen';
import { EditPageLoaderServer } from '@confluence/full-page-editor/entry-points/EditPageLoaderServer.ts';
import { shouldShowMobileWeb } from '@confluence/mobile-detection';
import {
	CONTAINER_HOME_ID,
	PRODUCT_HOME_ID,
} from '@confluence/app-navigation/entry-points/navigationIDs.ts';
import { COMPANY_HUB_EDIT, EDIT_PAGE_V2 } from '@confluence/named-routes/entry-points/routes.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { BannerContextProvider } from '@confluence/banners/entry-points/BannerContextProvider.ts';

import { parseDraftId } from '../helpers/editorUtilities.ts';

export const EditPageRoute = ({ name, params: { contentId, spaceKey }, query }: RouteMatch): React.JSX.Element | null => {
	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
	const shouldShowPremiumUpsell = useCompanyHubPremiumGate({
		skip: !isCompanyHub,
	});

	const contentType =
		name === EDIT_PAGE_V2.name || name === COMPANY_HUB_EDIT.name ? 'page' : 'blogpost';
	const draftShareId = parseDraftId(query);

	const children = (
		<BannerContextProvider>
			<EditPageLoaderServer
				contentId={contentId}
				spaceKey={spaceKey}
				draftShareId={draftShareId}
				contentType={contentType}
			/>
		</BannerContextProvider>
	);

	//
	// navView
	//
	let navView = CONTAINER_HOME_ID;
	if (isCompanyHub) {
		if (shouldShowPremiumUpsell) {
			return null;
		} else if (name === COMPANY_HUB_EDIT.name) {
			// CompanyHubEditRoute provides its own Screen and MainLayout.
			return children;
		}

		navView = PRODUCT_HOME_ID;
	}

	return (
		<Screen
			showPremiumOnboarding={false}
			pageState={{ contentId, spaceKey, contentType }}
			screenEvent={{
				name: 'editPageScreen',
				id: contentId,
			}}
		>
			<MainLayout
				enableNavigation={!shouldShowMobileWeb()}
				spaceKey={spaceKey}
				navView={navView}
				isEdit
			>
				{children}
			</MainLayout>
		</Screen>
	);
};
